/**
 * Schemas Zod de `/unidades` e `/equipes` (`03-endpoints-draft.md §3`).
 *
 * Três decisões que atravessam o arquivo:
 *
 *  1. **`.strict()` em todo corpo.** Campo desconhecido é `422`, não silêncio. Vale
 *     em especial para `responsavelId` em `/equipes`: a designação do responsável
 *     tem rota própria (`PUT /equipes/{id}/responsavel`) porque revoga sessões e
 *     entra na auditoria, e um `PATCH` genérico que a aceitasse contornaria as duas
 *     coisas.
 *  2. **Não existe `DELETE`.** Unidade e equipe em uso são desativadas (RN-41). O
 *     que se envia é `POST /{id}/desativar`.
 *  3. **`ativo` não entra no `PATCH`.** Ativar e desativar têm rota própria, porque
 *     desativar precisa conferir o que está vinculado (`ORGANIZACAO_EM_USO`) e
 *     registrar auditoria; permitir `PATCH { ativo: false }` daria dois caminhos
 *     para a mesma mudança de estado, um deles sem conferência.
 */
import { z } from 'zod'

import { zBooleanoFiltro, zBusca, zIdCatalogo, zUuid } from '../comum/campos'
import { schemaPaginacaoOffset } from '../comum/paginacao'

/**
 * Título de unidade, equipe e catálogo. Mínimo 2 para barrar "-" e "x"; a
 * unicidade entre ativos (RN-42, índice parcial no banco) é conferida pelo caso de
 * uso, não aqui — depende de consulta.
 */
export const zTituloOrganizacao = z
  .string({ required_error: 'Informe o título.' })
  .trim()
  .min(2, 'Informe ao menos 2 caracteres no título.')
  .max(120, 'Título muito longo.')

/** `ordem` é posição em lista, e a tela envia 0 para "primeiro". */
export const zOrdem = z.coerce
  .number()
  .int('A ordem precisa ser um número inteiro.')
  .min(0, 'A ordem não pode ser negativa.')
  .max(9_999, 'Ordem fora do intervalo.')

// ---------------------------------------------------------------------------
// Unidades — TELA-23
// ---------------------------------------------------------------------------

export const schemaCriarUnidade = z
  .object({
    titulo: zTituloOrganizacao,
    ordem: zOrdem.default(0),
  })
  .strict()

export type EntradaCriarUnidade = z.infer<typeof schemaCriarUnidade>

export const schemaAtualizarUnidade = z
  .object({
    titulo: zTituloOrganizacao.optional(),
    ordem: zOrdem.optional(),
  })
  .strict()
  .refine((valor) => Object.keys(valor).length > 0, 'Informe ao menos um campo para alterar.')

export type EntradaAtualizarUnidade = z.infer<typeof schemaAtualizarUnidade>

/** Ordenação permitida (allowlist — §6.6). Campo fora da lista → `422`. */
export const CAMPOS_ORDENACAO_UNIDADE = ['ordem', 'titulo', 'criadoEm'] as const

export type CampoOrdenacaoUnidade = (typeof CAMPOS_ORDENACAO_UNIDADE)[number]

export const schemaFiltrosUnidade = schemaPaginacaoOffset.extend({
  busca: zBusca,
  ativo: zBooleanoFiltro.optional(),
})

export type FiltrosUnidade = z.infer<typeof schemaFiltrosUnidade>

// ---------------------------------------------------------------------------
// Equipes — TELA-19
// ---------------------------------------------------------------------------

/**
 * RN-27: `null` é "NÃO FINALIZA", e é um valor legítimo — não ausência de valor. O
 * CHECK `equipes_ck_dias_para_finalizar` no banco espelha o limite.
 */
export const zDiasParaFinalizar = z.coerce
  .number()
  .int('Informe um número inteiro de dias.')
  .min(1, 'O mínimo é 1 dia. Para não finalizar automaticamente, deixe em branco.')
  .max(3_650, 'O máximo é 3650 dias.')
  .nullable()

export const schemaCriarEquipe = z
  .object({
    titulo: zTituloOrganizacao,
    /**
     * Nulável: a "Triagem" do legado é uma equipe sem unidade, que serve a todas
     * (ver `IntegridadeService.exigirEquipeNaUnidade`). Recusar aqui tornaria esse
     * arranjo impossível de cadastrar.
     */
    unidadeId: zIdCatalogo.nullish(),
    diasParaFinalizar: zDiasParaFinalizar.default(null),
    /**
     * C3 — responsável da equipe já na criação, quando se sabe quem é.
     *
     * Opcional porque a ordem natural do cadastro é: cria a equipe → vincula
     * pessoas → designa o responsável. Exigir aqui obrigaria a existir alguém já
     * membro de uma equipe que ainda não existe.
     */
    responsavelUuid: zUuid.nullish(),
  })
  .strict()

export type EntradaCriarEquipe = z.infer<typeof schemaCriarEquipe>

/**
 * `responsavelUuid` **não** está aqui de propósito: designar responsável é
 * `PUT /equipes/{id}/responsavel`, que confere I11, audita com valor anterior e novo
 * e é a decisão administrativa mais consequente do módulo (define para quem todo
 * lead novo da equipe chega).
 */
export const schemaAtualizarEquipe = z
  .object({
    titulo: zTituloOrganizacao.optional(),
    unidadeId: zIdCatalogo.nullish(),
    diasParaFinalizar: zDiasParaFinalizar.optional(),
  })
  .strict()
  .refine((valor) => Object.keys(valor).length > 0, 'Informe ao menos um campo para alterar.')

export type EntradaAtualizarEquipe = z.infer<typeof schemaAtualizarEquipe>

/**
 * `PUT /equipes/{id}/responsavel` — C3.
 *
 * `null` remove o responsável, e é operação legítima: o responsável saiu de
 * licença e ninguém assumiu ainda. Enquanto estiver nulo, o lead novo da equipe cai
 * na fila (RN-16) e o gestor é notificado — melhor do que apontar para quem não
 * está trabalhando.
 */
export const schemaDefinirResponsavelEquipe = z
  .object({ responsavelUuid: zUuid.nullable() })
  .strict()

export type EntradaDefinirResponsavelEquipe = z.infer<typeof schemaDefinirResponsavelEquipe>

export const CAMPOS_ORDENACAO_EQUIPE = ['titulo', 'criadoEm'] as const

export type CampoOrdenacaoEquipe = (typeof CAMPOS_ORDENACAO_EQUIPE)[number]

export const schemaFiltrosEquipe = schemaPaginacaoOffset.extend({
  busca: zBusca,
  ativo: zBooleanoFiltro.optional(),
  unidadeId: zIdCatalogo.optional(),
  /** C3 — "quais equipes estão sem responsável designado?" */
  semResponsavel: z.enum(['true']).optional(),
})

export type FiltrosEquipe = z.infer<typeof schemaFiltrosEquipe>

/**
 * `GET /equipes/{id}/atendentes-elegiveis?paraDistribuicao=` — RN-14.
 *
 * Sem a flag, a lista é a do select "Responsável" da TELA-03: todo atendente ativo
 * da equipe. Com `paraDistribuicao=true`, só quem tem `receber_leads = true` — que
 * é a leitura de "corretor de plantão" que o sistema tem hoje (C3).
 */
export const schemaFiltrosAtendentesElegiveis = z
  .object({ paraDistribuicao: z.enum(['true', 'false']).optional() })
  .strict()

export type FiltrosAtendentesElegiveis = z.infer<typeof schemaFiltrosAtendentesElegiveis>
